/// <reference types="@angular/localize" />


import { enableProdMode, ErrorHandler, provideAppInitializer, inject, importProvidersFrom } from '@angular/core';
import { environment } from './environments/environment';

import * as Sentry from "@sentry/angular";

import { Router } from '@angular/router';
import { AppConfig } from 'app/app.config';
import { AuthGuard } from 'app/modules/guard/auth.guard';
import { AccountantGuard } from 'app/modules/guard/accountant.guard';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from 'app/services/loader.service';
import { BlobDownload } from 'app/services/blob-download.service';
import { APIService } from 'app/services/api.service';
import { ExcelService } from 'app/services/excel.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { JwtInterceptor, ErrorInterceptor } from 'app/_helpers';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { DocumentModule } from 'app/modules/document/document.module';
import { NotificationsModule } from 'app/modules/notifications/notifications.module';
import { ProfileModule } from 'app/modules/profile/profile.module';
import { PortfolioModule } from 'app/modules/portfolio/portfolio.module';
import { ResourceModule } from 'app/modules/resource/resource.module';
import { AccountModule } from 'app/modules/account/account.module';
import { OtherModule } from 'app/modules/other/other.module';
import { ClientErrorModule } from 'app/modules/clienterror/client-error.module';
import { AdvisorModule } from 'app/modules/advisor/advisor.module';
import { NWMCharts } from 'app/components/NWMCharts/NWMCharts.module';
import { WelcomeModule } from 'app/modules/welcome/welcome.module';
import { PgfModule } from 'app/modules/pgf/pgf.module';
import { PgfIntroModule } from 'app/modules/pgf-intro/pgf-intro.module';
import { NWMControls } from 'app/components/Controls/NWMControls.module';
import { SharedModule } from 'app/modules/shared/shared.module';
import { AppRoutingModule } from 'app/app-routing.module';
import { AppComponent } from 'app/app.component';

if (environment.production) {
  enableProdMode();
}

if (environment.sentry) {
  Sentry.init({
    dsn: environment.sentry?.dsn,
    environment: environment.build?.env ?? "local",
    release: environment.build?.number ?? "dev",
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracePropagationTargets: ["localhost", "https://clwebqa.nicolawealth.com/webapi", "https://clwebstg.nicolawealth.com/webapi", "https://clientaccess.nicolawealth.com/webapi"],
    tracesSampleRate: +(environment.sentry?.tracesSampleRate) || 1.0,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      DocumentModule,
      NotificationsModule,
      ProfileModule,
      PortfolioModule,
      ResourceModule,
      AccountModule,
      OtherModule,
      ClientErrorModule,
      AdvisorModule,
      NWMCharts,
      WelcomeModule,
      PgfModule,
      PgfIntroModule,
      NWMControls,
      SharedModule.forRoot(),
      AppRoutingModule
    ),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => { inject(Sentry.TraceService); }),
    AppConfig,
    AuthGuard,
    AccountantGuard,
    AuthenticationService,
    LoaderService,
    BlobDownload,
    APIService,
    ExcelService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
}).then();
